import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError } from "../utils/errorHandler";
import {
  Box,
  Container,
  Tooltip,
  Autocomplete,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CssBaseline,
  Paper,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function TicketsDetails() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const today = getToday();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    env_id: "",
    app_id: "",
    status: "",
    priority: "",
  });

  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);

  const [visibleColumns, setVisibleColumns] = useState({
    ticket_id: false,
    cl_id: true,
    env_id: true,
    app_id: true,
    incident_number: true,
    ticket_name: true,
    status: true,
    priority: true,
    assignment_group: true,
    assignee: true,
    opened_date: true,
    description: true,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ticket_id: "",
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    incident_number: "",
    ticket_name: "",
    status: "",
    priority: "",
    assignment_group: "",
    assignee: "",
    description: "",
    opened_date: today,
  });

  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTicketId, setDeleteTicketId] = useState(null);
  const [exportError, setExportError] = useState("");

  // Fixed: Missing state declaration for Export dialog
  const [openExportDialog, setOpenExportDialog] = useState(false);

  const [exportFilters, setExportFilters] = useState({
    env_id: "",
    app_id: "",
    date_from: today,
    date_to: today,
  });

  const [exportType, setExportType] = useState("summary");

  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  // Column filter states
  const [columnFilters, setColumnFilters] = useState({});
  const [tempColumnFilters, setTempColumnFilters] = useState({});
  const [filterAnchorEls, setFilterAnchorEls] = useState({});

  const columnLabels = {
    ticket_id: "Ticket ID",
    cl_id: "Client",
    env_id: "Environment",
    app_id: "Application",
    incident_number: "Incident Number",
    ticket_name: "Ticket Name",
    status: "Status",
    priority: "Priority",
    assignment_group: "Assignment Group",
    assignee: "Assignee",
    opened_date: "Opened Date",
    description: "Description",
  };

  const columns = Object.keys(columnLabels);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    // Extract date part only (YYYY-MM-DD) to avoid timezone issues
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    const date = new Date(year, month - 1, day); // Create date in local timezone
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const fetchClients = useCallback(async () => {
    try {
      const data = await apiService.get("/tickets/clients");
      setClients(data);
    } catch (err) {
      console.error("Error loading clients:", err);
    }
  }, []);

  const fetchEnvironments = useCallback(async () => {
    try {
      const data = await apiService.get("/tickets/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error loading environments:", err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const data = await apiService.get("/tickets/applications");
      setApplications(data);
    } catch (err) {
      console.error("Error loading applications:", err);
    }
  }, []);

  // Cascading validation functions
  const fetchApplicationsByEnvironment = async (envId) => {
    if (!envId) {
      setFilteredApplications([]);
      return;
    }
    try {
      const result = await apiService.get(
        `/tickets/applications?env_id=${envId}`,
      );
      setFilteredApplications(result || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setFilteredApplications([]);
    }
  };
  // Environment change

  const handleEnvironmentChange = async (event) => {
    const envId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Clear application when environment changes
    }));

    // Clear validation errors for environment and application
    setFormErrors((prev) => ({
      ...prev,
      env_id: "",
      app_id: "",
    }));

    // Clear and fetch applications for the selected environment
    setFilteredApplications([]);
    if (envId) {
      await fetchApplicationsByEnvironment(envId);
    }
  };

  const handleApplicationChange = (event) => {
    const appId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      app_id: appId,
    }));

    // Clear validation error for application
    setFormErrors((prev) => ({
      ...prev,
      app_id: "",
    }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      limit: rowsPerPage.toString(),
    });

    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    if (filters.env_id) params.append("env_id", filters.env_id);
    if (filters.app_id) params.append("app_id", filters.app_id);
    if (filters.status) params.append("status", filters.status);
    if (filters.priority) params.append("priority", filters.priority);

    // Add column filters
    Object.keys(columnFilters).forEach((key) => {
      if (columnFilters[key] && columnFilters[key].trim() !== "") {
        params.append(key, columnFilters[key]);
      }
    });

    try {
      const result = await apiService.get(`/tickets/paginate/list?${params}`);
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, columnFilters]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  // Update client when clients are loaded and form is open for adding
  useEffect(() => {
    if (
      clients.length > 0 &&
      (!formData.cl_id || !clients.find((c) => c.cl_id === formData.cl_id))
    ) {
      const first = clients[0];
      setFormData((prev) => ({
        ...prev,
        cl_id: first.cl_id,
        client_name: first.name || "",
      }));
    }
  }, [clients, formData.cl_id]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: today,
      date_to: today,
      env_id: "",
      app_id: "",
      status: "",
      priority: "",
    });
    setPage(1);
  };

  // Column filter handlers
  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyColumnFilter = (column) => {
    setColumnFilters((prev) => ({ ...prev, [column]: tempColumnFilters[column] || "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearColumnFilter = (column) => {
    setTempColumnFilters((prev) => ({ ...prev, [column]: "" }));
    setColumnFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleExport = async () => {
    setExportError(""); // clear old error

    const params = new URLSearchParams({
      date_from: exportFilters.date_from,
      date_to: exportFilters.date_to,
    });

    if (exportFilters.env_id) params.append("env_id", exportFilters.env_id);
    if (exportFilters.app_id) params.append("app_id", exportFilters.app_id);

    const endpoint =
      exportType === "summary"
        ? `/tickets/export/summary?${params}`
        : `/tickets/export/detailed?${params}`;

    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "GET",
        headers: apiService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Export failed");
      }

      const text = await response.text();

      // Split into rows
      const rows = text.split("\n").filter((row) => row.trim() !== "");

      // If only header exists OR empty
      if (rows.length <= 1) {
        setExportError("No data found for this date range");
        return;
      }

      // Convert text back to blob for download
      const blob = new Blob([text], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        exportType === "summary"
          ? `Ticket_Summary_${exportFilters.date_from}_to_${exportFilters.date_to}.csv`
          : `Detailed_Incidents_${exportFilters.date_from}_to_${exportFilters.date_to}.csv`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setOpenExportDialog(false);
    } catch (error) {
      setExportError(error.message || "Failed to export");
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.cl_id) errors.cl_id = "Client is required";
    if (!formData.env_id) errors.env_id = "Environment is required";
    if (!formData.app_id) errors.app_id = "Application is required";
    if (!formData.incident_number)
      errors.incident_number = "Incident Number is required";
    if (!formData.ticket_name) errors.ticket_name = "Ticket Name is required";
    if (!formData.opened_date) errors.opened_date = "Opened Date is required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      let updatedTicket;
      if (isEditing) {
        updatedTicket = await apiService.put(
          `/tickets/${formData.ticket_id}`,
          formData,
        );

        // Update the ticket in local state to avoid refetching
        setData((prevData) =>
          prevData.map((ticket) =>
            ticket.ticket_id === formData.ticket_id
              ? {
                  ...ticket,
                  ...formData,
                  client_name: formData.client_name,
                  environment_name:
                    environments.find((env) => env.env_id === formData.env_id)
                      ?.name || formData.env_id,
                  application_name:
                    filteredApplications.find(
                      (app) => app.app_id === formData.app_id,
                    )?.name || formData.app_id,
                }
              : ticket,
          ),
        );
      } else {
        await apiService.post("/tickets", formData);
        // For new tickets, we need to refetch to get the complete data
        fetchData();
      }

      setSuccessMessage(
        isEditing
          ? "Ticket updated successfully!"
          : "Ticket added successfully!",
      );
      setOpenDialog(false);
      resetForm();

      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(err, "Ticket")
        : handleCreateError(err, "Ticket");
      setFormErrors({ general: errorMessage });
      setTimeout(() => setFormErrors({}), 3000);
    }
  };

  const resetForm = () => {
    // Set default client when resetting form
    let initialClient = {};
    if (clients.length > 0) {
      const first = clients[0];
      initialClient = {
        cl_id: first.cl_id,
        client_name: first.name || "",
      };
    }

    setFormData({
      ticket_id: "",
      ...initialClient,
      env_id: "",
      app_id: "",
      incident_number: "",
      ticket_name: "",
      status: "",
      priority: "",
      assignment_group: "",
      assignee: "",
      description: "",
      opened_date: today,
    });
    setIsEditing(false);
    setFormErrors({});
    setFilteredApplications([]);
  };

  const handleEdit = async (row) => {
    // Find the client name for the row's cl_id
    const client = clients.find((c) => c.cl_id === row.cl_id);
    const clientName = client ? client.name : row.cl_id;

    const editFormData = {
      ...row,
      client_name: clientName, // Set the client name for display
      opened_date: row.opened_date ? row.opened_date.split("T")[0] : today,
    };

    setFormData(editFormData);
    setOriginalFormData({ ...editFormData }); // Store original data for comparison
    setIsFormChanged(false); // Reset change flag
    setIsEditing(true);

    // Load applications for the selected environment when editing
    if (row.env_id) {
      await fetchApplicationsByEnvironment(row.env_id);
    }

    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    setDeleteTicketId(id);
    setOpenDeleteDialog(true);
  };

  const handleToggleFlag = async (id, currentFlag) => {
    try {
      await apiService.put(`/tickets/${id}`, {
        active_flag: currentFlag === 1 ? 0 : 1,
      });
      fetchData();
      
      // Show success message
      const message = currentFlag === 0 
        ? "Ticket Activated" 
        : "Ticket Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Use centralized error handler
      const errorMessage = handleUpdateError(err, "Ticket");
      setFormErrors({ general: errorMessage });
      setTimeout(() => setFormErrors({}), 3000);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Filters on left, Action Buttons on right - SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Left side: Filters */}
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.date_from}
              onChange={(e) => handleFilterChange("date_from", e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  mt: 0,
                  fontSize: "0.7rem",
                  top: "0.5px",
                },
              }}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent", 
                },
              }}
            />

            {filters.date_from && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDisplayDate(filters.date_from)}
              </Typography>
            )}
          </Box>

          <Box sx={{ position: "relative", minWidth: 140 }}>
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.date_to}
              onChange={(e) => handleFilterChange("date_to", e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: {
                  mt: 0,
                  fontSize: "0.7rem",
                  top: "1px",
                },
              }}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "transparent",
                },
              }}
            />

            {filters.date_to && (
              <Typography
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-10%)",
                  fontSize: "0.75rem",
                  color: "#000",
                  pointerEvents: "none",
                }}
              >
                {formatDisplayDate(filters.date_to)}
              </Typography>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Environment</InputLabel>
            <Select
              value={filters.env_id}
              label="Environment"
              onChange={(e) => handleFilterChange("env_id", e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
            >
              {environments.map((env) => (
                <MenuItem
                  key={env.env_id}
                  value={env.env_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Application</InputLabel>
            <Select
              value={filters.app_id}
              label="Application"
              onChange={(e) => handleFilterChange("app_id", e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.75rem",
                },
              }}
            >
              {applications.map((app) => (
                <MenuItem
                  key={app.app_id}
                  value={app.app_id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Add + */}
            <Button
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Clear Filters */}
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Export */}
            <Tooltip title="Export to CSV">
              <IconButton
                onClick={() => {
                  setExportError("");
                  setOpenExportDialog(true);
                }}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general}
        </Alert>
      )}

      {/* Table */}
      <Box
        sx={{
          height: "calc(90vh - 160px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {columnLabels[col]}
                              <IconButton
                                size="small"
                                onClick={(e) => handleFilterIconClick(e, col)}
                              >
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempColumnFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempColumnFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearColumnFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyColumnFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.ticket_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(row.ticket_id, row.active_flag)
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    row.active_flag === 1
                                      ? "#2E9F50"
                                      : "#FF0C0C",
                                  width: 17,
                                  height: 17,
                                  transition: "color 0.2s",
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.ticket_id)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.ticket_id && (
                          <TableCell>{row.ticket_id}</TableCell>
                        )}
                        {visibleColumns.cl_id && (
                          <TableCell>{row.client_name}</TableCell>
                        )}
                        {visibleColumns.env_id && (
                          <TableCell>{row.environment_name}</TableCell>
                        )}
                        {visibleColumns.app_id && (
                          <TableCell>{row.application_name}</TableCell>
                        )}
                        {visibleColumns.incident_number && (
                          <TableCell>{row.incident_number || "-"}</TableCell>
                        )}
                        {visibleColumns.ticket_name && (
                          <TableCell>{row.ticket_name || "-"}</TableCell>
                        )}
                        {visibleColumns.status && (
                          <TableCell>{row.status || "-"}</TableCell>
                        )}
                        {visibleColumns.priority && (
                          <TableCell>{row.priority || "-"}</TableCell>
                        )}
                        {visibleColumns.assignment_group && (
                          <TableCell>{row.assignment_group || "-"}</TableCell>
                        )}
                        {visibleColumns.assignee && (
                          <TableCell>{row.assignee || "-"}</TableCell>
                        )}
                        {visibleColumns.opened_date && (
                          <TableCell>
                            {formatDisplayDate(row.opened_date)}
                          </TableCell>
                        )}
                        {visibleColumns.description && (
                          <TableCell>
                            <Tooltip title={row.description || ""} arrow>
                              <span>
                                {row.description && row.description.length > 50
                                  ? `${row.description.substring(0, 50)}...`
                                  : row.description || "-"}
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No tickets found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>

      {/* Add/Edit Dialog */}
      {/* Add/Edit Dialog */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          maxWidth="md"
          fullWidth
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
        >
          <DialogTitle>
            {isEditing ? "Edit Ticket" : "Add New Ticket"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2, // reduced from 3 → tighter layout
                mt: 1,
                alignItems: "flex-start",
                // Global overrides for smaller size
                "& .MuiInputBase-root": {
                  height: 40, // ← target compact height
                  minHeight: 40,
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  paddingTop: 0,
                  paddingBottom: 0,
                  "& .MuiSelect-select": {
                    padding: "8.5px 14px", // reduced vertical padding for small size
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "8.5px 14px", // match TextField small padding
                  fontSize: "0.875rem", // slightly smaller text → feels more compact
                },
                "& .MuiFormHelperText-root": {
                  minHeight: "16px", // reserve space
                  marginTop: 2,
                  fontSize: "0.7rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem", // smaller labels
                  transform: "translate(14px, 9px) scale(1)", // adjust position for small
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                  },
                },

                "& .MuiFormHelperText-root": {
                  marginTop: "1px !important", // reduce gap
                  lineHeight: "1",
                  fontSize: "0.7rem",
                },
              }}
            >
              {/* Row 1: Client ID and Environment */}
              <TextField
                label="Client ID"
                value={formData.client_name || "Loading..."}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiInputBase-input": {
                    cursor: "not-allowed",
                    color: "grey",
                  },
                }}
                required
                fullWidth
                size="small" // ← key change: small
              />

              <FormControl fullWidth error={!!formErrors.env_id}>
                <Autocomplete
                  options={environments || []} // ensure array
                  getOptionLabel={(option) =>
                    option.name || option.env_id || ""
                  }
                  value={
                    environments?.find(
                      (env) => env.env_id === formData.env_id,
                    ) || null
                  }
                  onChange={async (event, selectedEnv) => {
                    const envId = selectedEnv?.env_id || "";

                    // Update form data
                    setFormData((prev) => ({
                      ...prev,
                      env_id: envId,
                      app_id: "", // clear application
                    }));

                    // Clear validation errors
                    setFormErrors((prev) => ({
                      ...prev,
                      env_id: "",
                      app_id: "",
                    }));

                    // Clear previous applications
                    setFilteredApplications([]);

                    // Fetch applications for selected environment
                    if (envId) {
                      await fetchApplicationsByEnvironment(envId);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Environment"
                      required
                      error={!!formErrors.env_id}
                      helperText={formErrors.env_id}
                      size="small"
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  )}
                  disableClearable
                  fullWidth
                />
              </FormControl>

              {/* Row 2: Application and Incident Number */}
              <FormControl fullWidth error={!!formErrors.app_id}>
                <Autocomplete
                  options={filteredApplications || []}
                  getOptionLabel={(option) =>
                    option.name || option.app_id || ""
                  }
                  value={
                    filteredApplications?.find(
                      (app) => app.app_id === formData.app_id,
                    ) || null
                  }
                  onChange={(event, selectedApp) => {
                    const appId = selectedApp?.app_id || "";

                    // Update form data
                    setFormData((prev) => ({
                      ...prev,
                      app_id: appId,
                    }));

                    // Clear validation error
                    setFormErrors((prev) => ({
                      ...prev,
                      app_id: "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Application"
                      required
                      error={!!formErrors.app_id}
                      helperText={formErrors.app_id}
                      size="small"
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
                    />
                  )}
                  disableClearable
                  fullWidth
                  disabled={
                    !formData.env_id || filteredApplications.length === 0
                  }
                  noOptionsText={
                    !formData.env_id
                      ? "Please select environment"
                      : "No applications available"
                  }
                />
              </FormControl>

              <TextField
                label="Incident Number"
                required
                value={formData.incident_number || ""}
                onChange={(e) => {
                  setFormData((p) => ({
                    ...p,
                    incident_number: e.target.value,
                  }));
                  // Clear validation error for incident number
                  setFormErrors((prev) => ({
                    ...prev,
                    incident_number: "",
                  }));
                }}
                fullWidth
                size="small"
                error={!!formErrors.incident_number}
                helperText={formErrors.incident_number}
              />

              {/* Row 3: Ticket Name and Status */}
              <TextField
                label="Ticket Name"
                value={formData.ticket_name || ""}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, ticket_name: e.target.value }));
                  // Clear validation error for incident number
                  setFormErrors((prev) => ({
                    ...prev,
                    ticket_name: "",
                  }));
                }}
                required
                error={!!formErrors.ticket_name}
                helperText={formErrors.ticket_name}
                fullWidth
                size="small"
              />

              <FormControl fullWidth size="small" error={!!formErrors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || ""}
                  label="Status"
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, status: e.target.value }));
                    setFormErrors((prev) => ({
                      ...prev,
                      status: "",
                    }));
                  }}
                  sx={{ fontSize: "0.875rem" }}
                >
                  <MenuItem value="Open" sx={{ fontSize: "0.875rem" }}>
                    Open
                  </MenuItem>
                  <MenuItem value="In Progress" sx={{ fontSize: "0.875rem" }}>
                    In Progress
                  </MenuItem>
                  <MenuItem value="On Hold" sx={{ fontSize: "0.875rem" }}>
                    On Hold
                  </MenuItem>
                  <MenuItem value="Resolved" sx={{ fontSize: "0.875rem" }}>
                    Resolved
                  </MenuItem>
                  <MenuItem value="Closed" sx={{ fontSize: "0.875rem" }}>
                    Closed
                  </MenuItem>
                  <MenuItem value="Canceled" sx={{ fontSize: "0.875rem" }}>
                    Canceled
                  </MenuItem>
                </Select>

                {formErrors.status && (
                  <FormHelperText>{formErrors.status || " "}</FormHelperText>
                )}
              </FormControl>

              {/* Row 4: Priority and Assignment Group */}
              <FormControl fullWidth error={!!formErrors.priority}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority || ""}
                  label="Priority"
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, priority: e.target.value }));
                    setFormErrors((prev) => ({
                      ...prev,
                      priority: "",
                    }));
                  }}
                  sx={{
                    fontSize: "0.875rem",
                    "& .MuiMenuItem-root": { fontSize: "0.875rem" },
                  }}
                  size="small"
                >
                  <MenuItem value="Low" sx={{ fontSize: "0.875rem" }}>
                    Low
                  </MenuItem>
                  <MenuItem value="Medium" sx={{ fontSize: "0.875rem" }}>
                    Medium
                  </MenuItem>
                  <MenuItem value="High" sx={{ fontSize: "0.875rem" }}>
                    High
                  </MenuItem>
                  <MenuItem value="Critical" sx={{ fontSize: "0.875rem" }}>
                    Critical
                  </MenuItem>
                </Select>
                {formErrors.priority && (
                  <FormHelperText>{formErrors.priority}</FormHelperText>
                )}
              </FormControl>

              <TextField
                label="Assignment Group"
                value={formData.assignment_group || ""}
                onChange={(e) => {
                  setFormData((p) => ({
                    ...p,
                    assignment_group: e.target.value,
                  }));
                  setFormErrors((prev) => ({
                    ...prev,
                    assignment_group: "",
                  }));
                }}
                fullWidth
                error={!!formErrors.assignment_group}
                helperText={formErrors.assignment_group}
                size="small"
              />

              {/* Row 5: Assignee and Opened Date */}
              <TextField
                label="Assignee"
                value={formData.assignee || ""}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, assignee: e.target.value }));
                  setFormErrors((prev) => ({
                    ...prev,
                    assignee: "",
                  }));
                }}
                fullWidth
                error={!!formErrors.assignee}
                helperText={formErrors.assignee}
                size="small"
              />

              <TextField
                label="Opened Date"
                type="date"
                value={formData.opened_date}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, opened_date: e.target.value }));
                  // Clear validation error for opened date
                  setFormErrors((prev) => ({
                    ...prev,
                    opened_date: "",
                  }));
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
                error={!!formErrors.opened_date}
                helperText={formErrors.opened_date}
                size="small"
              />

              {/* Row 6: Description (full width) – made less tall */}
              <TextField
                label="Description"
                multiline
                minRows={2} // reduced from 3
                maxRows={4}
                value={formData.description || ""}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, description: e.target.value }));
                  // Clear validation error for opened date
                  setFormErrors((prev) => ({
                    ...prev,
                    description: "",
                  }));
                }}
                fullWidth
                error={!!formErrors.description}
                helperText={formErrors.description}
                size="small"
                sx={{
                  gridColumn: "1 / -1",
                  "& .MuiInputBase-root": {
                    alignItems: "flex-start",
                    minHeight: 68, // smaller than before (was 100)
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={isEditing && !isFormChanged}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteTicketId(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Ticket{" "}
              <strong>
                {data.find((t) => t.ticket_id === deleteTicketId)
                  ?.ticket_name || deleteTicketId}
              </strong>{" "}
              ?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteTicketId(null);
              }}
              sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(`/tickets/${deleteTicketId}`);
                  fetchData();
                  setSuccessMessage("Ticket deleted successfully!");
                  setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                  // Use centralized error handler
                  const errorMessage = handleDeleteError(err, "Ticket");
                  setFormErrors({ general: errorMessage });
                  setTimeout(() => setFormErrors({}), 5000);
                } finally {
                  setOpenDeleteDialog(false);
                  setDeleteTicketId(null);
                }
              }}
              sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Export Tickets
          <IconButton
            onClick={() => setOpenExportDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {exportError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {exportError}
            </Alert>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mt: 1,
              alignItems: "center",

              "& .MuiInputBase-root": {
                height: 40,
              },
              "& .MuiInputBase-input": {
                fontSize: "0.875rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
              },
            }}
          >
            {/* Export Type - Full Width */}
            <FormControl fullWidth size="small" sx={{ gridColumn: "1 / -1" }}>
              <InputLabel>Export Type</InputLabel>
              <Select
                value={exportType}
                label="Export Type"
                onChange={(e) => setExportType(e.target.value)}
              >
                <MenuItem value="summary">
                  Summary (Count by Ticket Name)
                </MenuItem>
                <MenuItem value="detailed">Detailed Incidents List</MenuItem>
              </Select>
            </FormControl>

            {/* Start Date */}
            <TextField
              label="Start Date"
              type="date"
              value={exportFilters.date_from}
              onChange={(e) =>
                setExportFilters((p) => ({
                  ...p,
                  date_from: e.target.value,
                }))
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* End Date */}
            <TextField
              label="End Date"
              type="date"
              value={exportFilters.date_to}
              onChange={(e) =>
                setExportFilters((p) => ({
                  ...p,
                  date_to: e.target.value,
                }))
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* Environment */}
            <FormControl fullWidth size="small">
              <InputLabel>Environment</InputLabel>
              <Select
                value={exportFilters.env_id}
                label="Environment"
                onChange={(e) =>
                  setExportFilters((p) => ({
                    ...p,
                    env_id: e.target.value,
                  }))
                }
              >
                {/* <MenuItem value="">All Environments</MenuItem> */}
                {environments.map((env) => (
                  <MenuItem key={env.env_id} value={env.env_id}>
                    {env.name || env.env_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Application */}
            <FormControl fullWidth size="small">
              <InputLabel>Application</InputLabel>
              <Select
                value={exportFilters.app_id}
                label="Application"
                onChange={(e) =>
                  setExportFilters((p) => ({
                    ...p,
                    app_id: e.target.value,
                  }))
                }
              >
                {/* <MenuItem value="">All Applications</MenuItem> */}
                {applications.map((app) => (
                  <MenuItem key={app.app_id} value={app.app_id}>
                    {app.name || app.app_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Helper Text - Full Width */}
            <Typography variant="caption" sx={{ gridColumn: "1 / -1", mt: 1 }}>
              {exportType === "summary"
                ? "Exports grouped summary by Ticket Name with applied filters"
                : "Exports full list of tickets with applied filters"}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={!exportFilters.date_from || !exportFilters.date_to}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={() => setSettingsAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 260,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                px: 2,
                py: 1,
                border: "1px solid #ccc",
                borderRadius: 1.5,
                bgcolor: "#fff",
              }}
            >
              <Typography fontSize="0.8rem" fontWeight="bold">
                {columnLabels[col]}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((p) => ({ ...p, [col]: e.target.checked }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
}

export default TicketsDetails;
